/**
 * DuplicateAccountsGate — after login, if another account has the user's name but another email,
 * the user must settle it before continuing: merge the two, keep one, or say it is someone else.
 * Merging or keeping one needs a code sent to the other email (proof that both are theirs).
 */
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, RadioGroup, Radio,
  FormControlLabel, TextField, Alert, CircularProgress, Chip,
} from '@mui/material';
import { axiosService, fetcher } from 'shared/helpers/axios';

export default function DuplicateAccountsGate({ onSettled }) {
  const { data, mutate } = useSWR('/account/duplicates/', fetcher, { revalidateOnFocus: false });
  const dup = data?.results?.[0];
  const [step, setStep] = useState('ask');          // ask | choose | code
  const [action, setAction] = useState('merge');
  const [keep, setKeep] = useState('this');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {   // a fresh start for each duplicate
    setStep('ask'); setAction('merge'); setKeep('this'); setCode(''); setSentTo(''); setError('');
  }, [dup?.id]);

  if (!dup) return null;

  const call = async (fn) => {
    setBusy(true); setError('');
    try { return await fn(); } catch (e) {
      setError(e.response?.data?.error || (e.response?.status === 429 ? 'Too many codes requested. Try again later.' : 'Something went wrong.'));
      return null;
    } finally { setBusy(false); }
  };

  const notMe = () => call(async () => { await axiosService.post(`/account/duplicates/${dup.id}/not-me/`); await mutate(); });
  const sendCode = () => call(async () => {
    const r = await axiosService.post(`/account/duplicates/${dup.id}/send-code/`);
    setSentTo(r.data.sent_to); setStep('code');
  });
  const confirm = () => call(async () => {
    const r = await axiosService.post(`/account/duplicates/${dup.id}/resolve/`, { code, action, keep });
    if (r.data.logout) {
      await axiosService.post('/auth/logout/').catch(() => {});
      window.location.assign(`/login/?account=${encodeURIComponent(r.data.kept_email || '')}`);
      return;
    }
    await mutate();
    onSettled?.();
  });

  const otherLabel = `${dup.email}${dup.kind === 'invitation' ? ' (an invitation sent to you)' : ''}`;
  const keepThisLabel = `${data.your_email} (the account you are signed in with)`;

  return (
    <Dialog open disableEscapeKeyDown maxWidth="sm" fullWidth onClose={() => {}}>
      <DialogTitle>Is this account also yours?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          We found another Otable account with your name, {dup.name}, under a different email:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography fontWeight={700}>{dup.email}</Typography>
          <Chip size="small" label={dup.kind === 'invitation' ? 'Invitation' : 'Account'} />
        </Box>

        {step === 'ask' && (
          <Typography variant="body2" color="text.secondary">
            One person should have one account, so your consortia, proposals and contacts are all in one place.
            If this is you, you'll merge the two or keep one. If it is someone else with the same name, just say so.
          </Typography>
        )}

        {step === 'choose' && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>What should happen?</Typography>
            <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
              <FormControlLabel value="merge" control={<Radio />}
                label="Merge them: move everything from the other account into one (recommended)" />
              <FormControlLabel value="keep_one" control={<Radio />}
                label="Keep only one: deactivate the other account; nothing is moved" />
            </RadioGroup>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {action === 'merge' ? 'Which email should your account use?' : 'Which account do you keep?'}
            </Typography>
            <RadioGroup value={keep} onChange={(e) => setKeep(e.target.value)}>
              <FormControlLabel value="this" control={<Radio />} label={keepThisLabel} />
              <FormControlLabel value="other" control={<Radio />} label={otherLabel} />
            </RadioGroup>
            {action === 'keep_one' && keep === 'other' && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                The account you are signed in with will be deactivated and you will be signed out. Sign in again with {dup.email}.
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 1.5 }}>
              To prove the other address is yours, we'll send a 6-digit code to {dup.email}.
            </Alert>
          </>
        )}

        {step === 'code' && (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>Enter the code we sent to {sentTo}. It is valid for 15 minutes.</Typography>
            <TextField autoFocus fullWidth label="6-digit code" value={code}
              inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            <Button size="small" sx={{ mt: 1 }} disabled={busy} onClick={sendCode}>Send a new code</Button>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'ask' && (
          <>
            <Button color="inherit" disabled={busy} onClick={notMe}>No, that's someone else</Button>
            <Button variant="contained" disabled={busy} onClick={() => setStep('choose')}>Yes, it's me</Button>
          </>
        )}
        {step === 'choose' && (
          <>
            <Button color="inherit" disabled={busy} onClick={() => setStep('ask')}>Back</Button>
            <Button variant="contained" disabled={busy} onClick={sendCode}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>Send code</Button>
          </>
        )}
        {step === 'code' && (
          <>
            <Button color="inherit" disabled={busy} onClick={() => setStep('choose')}>Back</Button>
            <Button variant="contained" disabled={busy || code.length !== 6} onClick={confirm}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
              {action === 'merge' ? 'Merge accounts' : 'Keep this choice'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
